import { useContext, useEffect, useMemo, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { useClientsData, useExchangesMetadata } from "@shared/utils/hooks/misc";
import { isSymbol } from "@shared/types/utils";
import { difference, intersection } from "lodash";
import { toggleArrayItem } from "@shared/utils/arrays";
import {
  BotFormType,
  Error,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { EXCHANGES } from "@shared/common/constants";
import { ALTERNATIVE_USD_PAIRS_TITLE } from "@shared/features/bots/BotForm/common/constants";
import { BotType, LanguageNameSpaces, OpenMessage } from "@shared/types";
import { useAccountAlternativeUsdPairs } from "@shared/features/configuration/hooks";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useAlternativeUsdPairsProps {
  openMessage?: OpenMessage;
}

const useAlternativeUsdPairs = ({
  openMessage,
}: useAlternativeUsdPairsProps) => {
  const {
    botType,
    alternativeUsdPairs,
    setAlternativeUsdPairs,
    account,
    checkedFields,
    toggleField,
    addError,
    removeError,
    botFormType,
    isLoading,
    botGeneralData,
    setIsAdvancedView,
    advancedSettings,
    setAdvancedSettings,
    isChannelConfiguration,
  } = useContext(BotFormContext)!;
  const { getAccountById, isSuccess: isClientsDataSuccess } = useClientsData();
  const { getAlternativeUsdPairs, isSuccess } = useExchangesMetadata();
  const parsedAccount = isSymbol(account) ? undefined : account;
  const accountId = parsedAccount?.client_id;
  const [initializedAccountId, setInitializedAccountId] = useState<number>();
  const shouldDisplay = !isChannelConfiguration;
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const isCreate = botFormType === BotFormType.CREATE;
  const isEdit = botFormType === BotFormType.EDIT;
  const isCopyPublic = botFormType === BotFormType.COPY_PUBLIC;
  const isDuplicate = botFormType === BotFormType.DUPLICATE;
  const isBasedOnOtherBot = isEdit || isCopyPublic || isDuplicate;
  const originalBotAccountId =
    botGeneralData && "client_id" in botGeneralData
      ? botGeneralData.client_id
      : undefined;
  const defaultIgnoreEditWarning = !isCopyPublic && !isDuplicate;
  const isChecked = checkedFields.has(ToggleField.ALTERNATIVE_USD_PAIRS);
  const isError = isChecked && !alternativeUsdPairs?.length;
  const {
    isDisabled,
    isInverse,
    isSinglePairWarning,
    isMicaCreditMode,
    isBitmex,
    isDefaultEnabled,
    getAccountData,
    singlePair,
  } = useAccountAlternativeUsdPairs(parsedAccount);
  const parsedAlternativeUsdPairs = isChecked ? alternativeUsdPairs : undefined;
  const originalBotAccount = getAccountById(originalBotAccountId);
  const originalBotAlternativeIsDefault =
    getAccountData(originalBotAccount).isDefaultEnabled;

  const [ignoreEditWarning, setIgnoreEditWarning] = useState(
    defaultIgnoreEditWarning
  );

  const originalBotAlternativePairs =
    botGeneralData && "alternative_usd_pairs" in botGeneralData
      ? botGeneralData.alternative_usd_pairs
      : undefined;

  const exchangeAlternativePairs = useMemo(() => {
    return getAlternativeUsdPairs(accountId) || [];
  }, [accountId, getAlternativeUsdPairs]);

  const setCooldownBetweenTrades = (value?: string) => {
    setAdvancedSettings({
      ...advancedSettings,
      cooldown_between_opening_trades_per_symbol: value,
    });
  };

  const setDefaultCooldown = () => {
    const previousCooldown =
      advancedSettings.cooldown_between_opening_trades_per_symbol;
    const parsedCooldown =
      previousCooldown && !isSymbol(previousCooldown)
        ? parseFloat(previousCooldown)
        : 0;
    const updatedCooldown = Math.max(60, parsedCooldown);
    setCooldownBetweenTrades(updatedCooldown.toString());
  };

  const getShouldInitialize = () => {
    const baseShouldInitialize =
      botType === BotType.SIGNAL &&
      isClientsDataSuccess &&
      isSuccess &&
      !isLoading &&
      (!initializedAccountId || initializedAccountId !== accountId);
    const initializeCreate = isCreate && baseShouldInitialize;
    const initializeEdit =
      isBasedOnOtherBot && !!botGeneralData && baseShouldInitialize;
    return initializeCreate || initializeEdit;
  };

  useEffect(() => {
    if (getShouldInitialize()) {
      let isToggledOn: boolean;
      let enableCooldown = false;
      let pairs: string[] | undefined;
      if (isCreate) {
        pairs = exchangeAlternativePairs;
        isToggledOn = isDefaultEnabled && !!pairs.length;
        enableCooldown = isToggledOn;
      } else if (isEdit) {
        pairs = originalBotAlternativePairs;
        isToggledOn = !!originalBotAlternativePairs?.length;
      } else if (
        isDefaultEnabled &&
        ((isDuplicate && !originalBotAlternativeIsDefault) || isCopyPublic)
      ) {
        pairs = exchangeAlternativePairs;
        isToggledOn = !!pairs.length;
        enableCooldown = isToggledOn;
      } else {
        pairs = intersection(
          originalBotAlternativePairs,
          exchangeAlternativePairs
        );
        isToggledOn = !!pairs.length;
      }

      if (isDisabled) isToggledOn = false;
      toggleField(ToggleField.ALTERNATIVE_USD_PAIRS, isToggledOn);
      if (!isToggledOn) pairs = exchangeAlternativePairs;
      setAlternativeUsdPairs(pairs);
      if (enableCooldown) setDefaultCooldown();
      setIgnoreEditWarning(defaultIgnoreEditWarning);
      setInitializedAccountId(accountId);
      if (
        (isDefaultEnabled && !isToggledOn) ||
        (!isDefaultEnabled && isToggledOn)
      ) {
        setIsAdvancedView(true);
      }
    }
  }, [isSuccess, isClientsDataSuccess, isLoading, accountId]);

  const resetShowEditWarning = () => {
    setIgnoreEditWarning(true);
  };

  const onPairClick = (pair: string) => {
    setAlternativeUsdPairs((pairs) => toggleArrayItem(pairs || [], pair));
    resetShowEditWarning();
  };

  const options = useMemo(() => {
    return exchangeAlternativePairs.map((pair) => {
      return {
        name: pair,
        selected: alternativeUsdPairs?.includes(pair),
        onClick: () => onPairClick(pair),
      };
    });
  }, [exchangeAlternativePairs, alternativeUsdPairs]);

  useEffect(() => {
    if (isError) {
      addError(Error.ALTERNATIVE_USD_PAIRS);
    } else {
      removeError(Error.ALTERNATIVE_USD_PAIRS);
    }
  }, [isError]);

  const errorMessage = isError ? "Please choose at least one pair." : undefined;

  const getWarning = () => {
    if (ignoreEditWarning) return;
    if (
      originalBotAlternativePairs?.length &&
      !parsedAlternativeUsdPairs?.length
    ) {
      const prefix = `The '${t(
        ALTERNATIVE_USD_PAIRS_TITLE
      )}' setting was turned off because`;
      if (isMicaCreditMode) {
        return `${prefix} the selected account is in MICA BNFCR mode.`;
      } else if (isSinglePairWarning) {
        return `${prefix} the selected exchange has only ${singlePair} symbols supported.`;
      } else if (isBitmex) {
        return `${prefix} it is not supported in the selected exchange (${EXCHANGES.BITMEX}).`;
      } else if (isInverse) {
        return `${prefix} it is not supported for inverse contracts on this exchange.`;
      }
      return `${prefix} all the original bot's selected pairs are not valid in the selected account.`;
    }
    const copyPublicWarningPairs = difference(
      originalBotAlternativePairs,
      parsedAlternativeUsdPairs || []
    );
    return copyPublicWarningPairs?.length
      ? `Some of the original bot's alternative USD pairs (${copyPublicWarningPairs.join(
          ", "
        )}) were removed because they are not valid in the selected account.`
      : undefined;
  };

  const onPersonalClick = () => {
    if (isMicaCreditMode) {
      openMessage?.(
        "The selected account is in MICA BNFCR mode and therefore this feature cannot be enabled.",
        "warning"
      );
    } else if (isSinglePairWarning) {
      openMessage?.(
        `The selected exchange has only ${singlePair} symbols supported and therefore this feature cannot be enabled.`,
        "warning"
      );
    } else if (isBitmex) {
      openMessage?.(
        "This feature currently cannot be enabled for BitMEX exchange accounts.",
        "warning"
      );
    } else if (isInverse) {
      openMessage?.(
        "This feature currently cannot be enabled for inverse contracts on this exchange.",
        "warning"
      );
    } else {
      toggleField(ToggleField.ALTERNATIVE_USD_PAIRS, true);
      setDefaultCooldown();
    }
  };

  return {
    options,
    errorMessage,
    warningMessage: getWarning(),
    shouldDisplay,
    resetShowEditWarning,
    onPersonalClick,
  };
};

export default useAlternativeUsdPairs;

// Initialization logic:
// 1. Create - reset to account default on every account change
// 2. Edit - Use original bot configuration, do not change anything
// 3. Duplicate -
//    a. Original bot not mica, current account mica - enable (with cooldown)
//    b. Original bot mica - Use original configuration, if enable use intersection of valid pairs,
//       disable if intersection is empty.
// 4. Copy Public -
//    a. Current account mica - always enable and ignore original pairs (enable all valid for account)
//    b. Current account not mica - Use original configuration, if enable use intersection of valid pairs,
//       disable if intersection is empty.
